// Khối dùng chung cho màn Hoa hồng CTV: chọn tháng, bảng bậc, thanh tiến độ, danh sách dòng
// hoa hồng theo đơn.

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { money } from '@/lib/format'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { HOLD_REASON_LABEL } from '@/components/orders/order-money'
import { LINE_STATUS, commissionNoteLabel, fmtDate, monthLabel, shiftMonth, tierRange, vnd, type CommissionLine, type CommissionTier } from './types'

/** ‹ Tháng 10/2026 › — không cho đi quá `max` (tháng hiện tại). */
export function MonthStepper({ value, onChange, max, className }: { value: string; onChange: (m: string) => void; max?: string; className?: string }) {
  const atMax = !!max && value >= max
  const btn =
    'motion-state grid size-11 place-items-center rounded-md text-inkSoft hover:bg-raise hover:text-ink disabled:pointer-events-none disabled:opacity-40 desktop:size-8'
  return (
    <div className={cn('inline-flex items-center rounded-lg bg-paperSoft p-0.5', className)} role="group" aria-label="Chọn tháng">
      <button type="button" className={btn} onClick={() => onChange(shiftMonth(value, -1))} aria-label="Tháng trước">
        <ChevronLeft className="size-4" aria-hidden />
      </button>
      <span className="min-w-[7.5rem] px-1 text-center text-sm font-medium tabular-nums text-ink" aria-live="polite">
        Tháng {monthLabel(value)}
      </span>
      <button type="button" className={btn} onClick={() => onChange(shiftMonth(value, 1))} disabled={atMax} aria-label="Tháng sau">
        <ChevronRight className="size-4" aria-hidden />
      </button>
    </div>
  )
}

/** Bảng bậc: đơn thứ k trong ngày (theo ngày tạo đơn) → hoa hồng mỗi đơn. Tô bậc đang đạt. */
export function TierTable({ tiers, currentIndex, currentLabel = 'Đang ở bậc này', className }: { tiers: CommissionTier[]; currentIndex?: number | null; currentLabel?: string; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-rule', className)}>
      <table className="w-full text-sm">
        <thead className="bg-paperSoft text-xs text-inkSoft">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Đơn thứ … trong ngày</th>
            <th className="px-3 py-2 text-right font-medium">Chiết khấu mỗi đơn</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-rule">
          {tiers.map((t, i) => {
            const active = currentIndex === i
            return (
              <tr key={i} className={cn(active && 'bg-accent/[0.07]')} aria-current={active ? 'true' : undefined}>
                <td className="px-3 py-2">
                  <span className={cn('tabular-nums', active ? 'font-semibold text-ink' : 'text-inkSoft')}>{tierRange(t)}</span>
                  {active && <span className="ml-2 rounded-full bg-accent/12 px-2 py-0.5 text-xs font-medium text-accent">{currentLabel}</span>}
                </td>
                <td className={cn('px-3 py-2 text-right font-mono tabular-nums', active ? 'font-semibold text-accent' : 'text-ink')}>{vnd(t.amountPerOrder)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function ProgressBar({ value, className, label }: { value: number; className?: string; label: string }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100)
  return (
    <div className={cn('h-2 w-full overflow-hidden rounded-full bg-paperSoft', className)} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
      <div className="h-full rounded-full bg-accent transition-[width] duration-500" style={{ width: `${pct}%` }} />
    </div>
  )
}

export function LineStatusBadge({ status, holdReason }: { status: string; holdReason?: string | null }) {
  const meta = LINE_STATUS[status] ?? { text: status, cls: 'bg-paperSoft text-inkSoft' }
  const reason = status === 'held' && holdReason ? HOLD_REASON_LABEL[holdReason] : null
  return (
    <span className={cn('inline-flex max-w-full items-start gap-1.5 rounded-full px-2 py-0.5 text-left text-xs font-medium', meta.cls)}>
      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      <span className="min-w-0 break-words">{reason ? `${meta.text} — ${reason}` : meta.text}</span>
    </span>
  )
}

/** Dòng hoa hồng theo đơn của một CTV trong tháng (tên shop của đơn do máy chủ trả kèm). */
export function CommissionLinesTable({
  lines,
  loading,
  ctvShopId,
}: {
  lines: CommissionLine[]
  loading?: boolean
  ctvShopId?: string
}) {
  const columns: DataTableColumn<CommissionLine>[] = [
    {
      key: 'waybill',
      header: 'Mã vận đơn',
      priority: 'primary',
      cell: (l) => (
        <span className="block min-w-0">
          <span className="block font-mono text-sm text-ink">{l.waybillCode ?? '—'}</span>
          {l.shopId && l.shopId !== ctvShopId && <span className="block text-xs text-muted">Đơn của {l.shopName ?? 'shop trực thuộc'}</span>}
          {l.shopId && l.shopId === ctvShopId && <span className="block text-xs text-muted">Đơn tự bán</span>}
        </span>
      ),
    },
    { key: 'pickedUp', header: 'Ngày lấy hàng', cell: (l) => <span className="tabular-nums text-inkSoft">{fmtDate(l.pickedUpAt ?? l.deliveredAt)}</span> },
    {
      key: 'source',
      header: 'Loại',
      cell: (l) => {
        const note = commissionNoteLabel(l.note)
        return (
          <span className="whitespace-nowrap text-inkSoft">
            {l.sourceRef === 'topup' ? 'Bù cuối tháng' : l.sourceRef === 'base' ? 'Theo đơn' : 'Điều chỉnh'}
            {note && <span className="block text-xs text-muted">{note}</span>}
          </span>
        )
      },
    },
    {
      key: 'amount',
      header: 'Chiết khấu',
      align: 'right',
      headerClassName: 'whitespace-nowrap',
      cell: (l) => <span className={cn('whitespace-nowrap font-mono font-semibold tabular-nums', l.amount < 0 ? 'text-danger' : 'text-ink')}>{l.amount > 0 ? '+' : ''}{money(l.amount)}</span>,
    },
    { key: 'status', header: 'Trạng thái', cell: (l) => <LineStatusBadge status={l.status} holdReason={l.holdReason} /> },
  ]
  return (
    <DataTable
      columns={columns}
      data={lines}
      rowKey={(l) => l.id}
      loading={loading}
      emptyLabel="Chưa có đơn"
    />
  )
}
